"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";

interface MenuItem {
  label: string;
  href: string;
  description?: string;
}

const byFunction: MenuItem[] = [
  { label: "Procurement",   href: "/solutions/procurement",  description: "Supplier management & sourcing intelligence" },
  { label: "Supply Chain",  href: "/solutions/supply-chain", description: "End-to-end visibility and execution layer" },
  { label: "Logistics",     href: "/solutions/logistics",    description: "Transport, freight & last-mile optimisation" },
];

const bySize: MenuItem[] = [
  { label: "Mid-Market",  href: "/solutions/mid-market", description: "Right-sized for 50–500 person ops teams" },
  { label: "Enterprise",  href: "/solutions/enterprise", description: "Global scale with custom data residency" },
];

const byType: MenuItem[] = [
  { label: "Manufacturing",             href: "/solutions/manufacturing",          description: "Plant & production supply chain" },
  { label: "Distribution & Wholesale",  href: "/solutions/distribution-wholesale", description: "Multi-echelon inventory & fulfilment" },
  { label: "Consulting Firms",          href: "/solutions/consulting-firms",       description: "Client delivery at practitioner speed" },
  { label: "Supply Chain Tech Companies", href: "/solutions/scm-tech-companies",     description: "Embed intelligence in your own product" },
];

function MegaMenuPanel({
  pathname,
  onClose,
  onMouseEnter,
  onMouseLeave,
}: {
  pathname: string;
  onClose: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) {
  // Portal target: the <nav> element (nearest .nav ancestor)
  const [navEl, setNavEl] = useState<Element | null>(null);

  useEffect(() => {
    setNavEl(document.querySelector(".nav-inner"));
  }, []);

  const panel = (
    <div
      className="mega-menu"
      role="menu"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="mega-menu-inner">
        {/* Col 1 — By Function */}
        <div className="mega-col">
          <p className="mega-col-heading">By Function</p>
          <ul className="mega-list">
            {byFunction.map((item) => (
              <li key={item.href}>
                <Link
                  href={item.href}
                  className={`mega-item${pathname === item.href ? " active" : ""}`}
                  role="menuitem"
                  onClick={onClose}
                >
                  <span className="mega-item-label">{item.label}</span>
                  {item.description && (
                    <span className="mega-item-desc">{item.description}</span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="mega-divider" aria-hidden="true" />

        {/* Col 2 — By Company Size */}
        <div className="mega-col">
          <p className="mega-col-heading">By Company Size</p>
          <ul className="mega-list">
            {bySize.map((item) => (
              <li key={item.href}>
                <Link
                  href={item.href}
                  className={`mega-item${pathname === item.href ? " active" : ""}`}
                  role="menuitem"
                  onClick={onClose}
                >
                  <span className="mega-item-label">{item.label}</span>
                  {item.description && (
                    <span className="mega-item-desc">{item.description}</span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="mega-divider" aria-hidden="true" />

        {/* Col 3 — By Company Type */}
        <div className="mega-col">
          <p className="mega-col-heading">By Company Type</p>
          <ul className="mega-list">
            {byType.map((item) => (
              <li key={item.href}>
                <Link
                  href={item.href}
                  className={`mega-item${pathname === item.href ? " active" : ""}`}
                  role="menuitem"
                  onClick={onClose}
                >
                  <span className="mega-item-label">{item.label}</span>
                  {item.description && (
                    <span className="mega-item-desc">{item.description}</span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="mega-divider" aria-hidden="true" />

        {/* Hub CTA */}
        <div className="mega-col mega-col--cta">
          <p className="mega-col-heading">Explore All</p>
          <Link
            href="/solutions"
            className="mega-hub-link"
            onClick={onClose}
          >
            <span className="mega-hub-title">Solutions Hub</span>
            <span className="mega-hub-desc">Browse all use cases and find the right fit</span>
            <span className="mega-hub-arrow" aria-hidden="true">→</span>
          </Link>
        </div>
      </div>
    </div>
  );

  if (!navEl) return null;
  return createPortal(panel, navEl);
}
/** Inline accordion variant used inside the mobile drawer */
function MobileSolutionsAccordion({
  pathname,
  onClose,
}: {
  pathname: string;
  onClose: () => void;
}) {
  const [open, setOpen] = useState(false);
  const isActive = pathname.startsWith("/solutions");

  const allItems = [
    { heading: "By Function", items: byFunction },
    { heading: "By Company Size", items: bySize },
    { heading: "By Company Type", items: byType },
  ];

  return (
    <div>
      <button
        type="button"
        className={`mobile-menu-trigger${open ? " is-open" : ""}${isActive ? " active" : ""}`}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
          <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
        </svg>
        Solutions
        <svg className="mobile-trigger-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {open && (
        <div className="mobile-submenu">
          {allItems.map(({ heading, items }) => (
            <div key={heading}>
              <p className="mobile-submenu-heading">{heading}</p>
              {items.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className={`mega-item${pathname === item.href ? " active" : ""}`}
                  onClick={onClose}
                >
                  <span className="mega-item-label">{item.label}</span>
                  {item.description && (
                    <span className="mega-item-desc">{item.description}</span>
                  )}
                </Link>
              ))}
            </div>
          ))}
          <p className="mobile-submenu-heading">Explore All</p>
          <Link href="/solutions" className="mega-item" onClick={onClose}>
            <span className="mega-item-label">Solutions Hub →</span>
          </Link>
        </div>
      )}
    </div>
  );
}

interface SolutionsMenuProps {
  /** Render as inline mobile accordion instead of hover portal */
  mobile?: boolean;
  onMobileClose?: () => void;
  /** Controlled open state (desktop only) */
  isOpen?: boolean;
  onToggle?: () => void;
}

export function SolutionsMenu({ mobile, onMobileClose, isOpen, onToggle }: SolutionsMenuProps) {
  const pathname = usePathname();
  // fallback to internal state when not controlled (e.g. mobile)
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isOpen ?? internalOpen;

  const isActive = pathname.startsWith("/solutions");

  const handleClose = () => {
    if (onToggle && isOpen) onToggle();
    else setInternalOpen(false);
  };

  if (mobile) {
    return (
      <MobileSolutionsAccordion
        pathname={pathname}
        onClose={onMobileClose ?? (() => {})}
      />
    );
  }

  return (
    <div className="services-trigger">
      <button
        type="button"
        className={`nav-link services-trigger-btn${isActive ? " active" : ""}${open ? " open" : ""}`}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={onToggle ?? (() => setInternalOpen((v) => !v))}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
          <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
        </svg>
        Solutions
        <svg className={`services-chevron${open ? " flipped" : ""}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {open && (
        <MegaMenuPanel
          pathname={pathname}
          onClose={handleClose}
          onMouseEnter={() => {}}
          onMouseLeave={() => {}}
        />
      )}
    </div>
  );
}
